/**
 * SH 99 Segment C — phase-advance update layer.
 *
 * Renders the newly phase-advanced Grand Parkway Segment C jobs as a
 * highlighted, animated line so they stand out from the static parcel layers.
 * Source: TxDOT_Projects_Info (pulled 2026-06-03; jobs advanced 2026-06-02).
 */

import mapboxgl from 'mapbox-gl';

const SRC_ID = 'sh99-segment-c-update';
const GLOW_ID = 'sh99-segment-c-glow';
const LINE_ID = 'sh99-segment-c-line';
const DASH_ID = 'sh99-segment-c-dash';
// Served statically from public/data/ (same pattern as the parcel layers)
const DATA_URL = '/data/sh99-segment-c-update.geojson';

// Full SH 99 Grand Parkway route (all jobs) — drawn as a calm base line
const ROUTE_SRC = 'sh99-route';
const ROUTE_CASE_ID = 'sh99-route-casing';
const ROUTE_LINE_ID = 'sh99-route-line';
const ROUTE_URL = '/data/sh99-grand-parkway-route.geojson';

// Constructed / open SH 99 — actual built roadbed centerline (TxDOT_Roadways).
// This is the authoritative "what exists" gray base; the tracker overlay
// (blue/yellow) draws on top of it.
const BUILT_SRC = 'sh99-built';
const BUILT_CASE_ID = 'sh99-built-casing';
const BUILT_LINE_ID = 'sh99-built-line';
const BUILT_URL = '/data/sh99-built-road.geojson';

const HIGHLIGHT = '#ffd400';   // bright amber — "new / hot"
const GLOW = '#ff3b30';        // red glow underlay

function fmtMoney(v) {
  if (v == null || v === '') return '—';
  return '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const blank = (v) =>
  v == null || v === '' || v === '.' || String(v).toUpperCase() === 'TBD';

// Emit a <tr> only when the value is present — keeps popups compact.
function row(label, val, opts = {}) {
  if (blank(val)) return '';
  const v = opts.bold ? `<b>${val}</b>` : val;
  return `<tr><td>${label}</td><td>${v}</td></tr>`;
}

// Shared detail block — renders every field carried on the enriched feature.
// Source: TxDOT_Projects_Info (live TxDOT_Projects_Info/FeatureServer/0).
function detailRows(p) {
  const limits = [p.From, p.To].some((x) => !blank(x))
    ? `${p.From || '—'} → ${p.To || '—'}` : null;
  const miles = !blank(p.Mile_Begin) || !blank(p.Mile_End)
    ? `${p.Mile_Begin ?? '—'} – ${p.Mile_End ?? '—'}` : null;
  const pct = blank(p.Pct_Complete) ? null : `${Math.round(Number(p.Pct_Complete))}%`;
  const contact = !blank(p.TxDOT_Contact)
    ? `${p.TxDOT_Contact}${blank(p.TxDOT_Phone) ? '' : ' · ' + p.TxDOT_Phone}` : null;
  const districts = ['US_Hse', 'State_Hse', 'State_Sen']
    .map((k) => p[k]).filter((x) => !blank(x)).length
    ? `US ${p.US_Hse || '—'} · TX-H ${p.State_Hse || '—'} · TX-S ${p.State_Sen || '—'}` : null;
  return [
    row('Limits', limits),
    row('County', p.County),
    row('Phase', p.Phase),
    row('Stage', p.Stage),
    row('Class', p.Class),
    row('Work', p.Work),
    row('Delivery', p.Delivery),
    row('Length', blank(p.Length_mi) ? null : `${p.Length_mi} mi`),
    row('Mile markers', miles),
    row('Est. cost', blank(p.Est_Cost) ? null : fmtMoney(p.Est_Cost)),
    row('Spent', !p.Spent ? null : fmtMoney(p.Spent)),
    row('% complete', pct),
    row('Est. let', p.Est_Let, { bold: true }),
    row('Actual let', p.Actual_Let),
    row('Award', p.Award),
    row('NTP', p.NTP),
    row('Work begin', p.Work_Begin),
    row('Est. complete', p.Est_Complete),
    row('Contractor', p.Contractor),
    row('TxDOT contact', contact),
    row('Districts', districts),
    row('Project ID', p.Project_Id),
    row('Fiscal yr', p.Fiscal_Yr),
    row('In UTP', p.UTP === 1 ? 'Yes' : (p.UTP === 0 ? 'No' : null)),
    row('NHS', p.NHS === 'Y' ? 'Yes' : null),
    row('Updated', p.Last_Update),
  ].join('');
}

function popupHtml(p) {
  const note = blank(p.Update) ? '' : `<div class="route-popup__note">${p.Update}</div>`;
  return `
    <div class="route-popup">
      <div class="route-popup__tag">NEW · CONSTRUCTION ≤ 4 YRS</div>
      <h3>SH 99 · CSJ ${p.CSJ}</h3>
      <table>${detailRows(p)}</table>
      ${note}
    </div>`;
}

function routePopupHtml(p) {
  const tag = p.is_new
    ? `<div class="route-popup__tag">NEW · CONSTRUCTION ≤ 4 YRS</div>`
    : '';
  return `
    <div class="route-popup">
      ${tag}
      <h3>SH 99 · CSJ ${p.CSJ}</h3>
      <table>${detailRows(p)}</table>
    </div>`;
}

// Which SH 99 classes are currently shown (driven by the legend checkboxes).
const routeEnabled = { constructed: true, planned: true, updated: true };

// Apply the current class filter to every SH 99 layer:
//   constructed → gray built-road base (layer visibility)
//   planned/updated → blue/yellow tracker overlay (feature filter)
//   updated also governs the animated Segment-C highlight
function applyRouteFilter(map) {
  const gray = routeEnabled.constructed ? 'visible' : 'none';
  [BUILT_CASE_ID, BUILT_LINE_ID].forEach((id) => {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', gray);
  });

  const on = [];
  if (routeEnabled.planned) on.push('planned');
  if (routeEnabled.updated) on.push('updated');
  const filt = ['in', ['get', 'style_class'], ['literal', on]];
  [ROUTE_CASE_ID, ROUTE_LINE_ID].forEach((id) => {
    if (map.getLayer(id)) map.setFilter(id, filt);
  });

  const yellow = routeEnabled.updated ? 'visible' : 'none';
  [GLOW_ID, LINE_ID, DASH_ID].forEach((id) => {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', yellow);
  });
}

// Open a route popup, first easing the clicked point toward map center so a
// tall (scrollable) popup always has room above/below and Mapbox's auto-anchor
// never has to place it where the window edge would clip it.
function openRoutePopup(map, lngLat, html, maxWidth = '360px') {
  map.easeTo({ center: lngLat, duration: 250 });
  new mapboxgl.Popup({ maxWidth, className: 'parcel-popup' })
    .setLngLat(lngLat)
    .setHTML(html)
    .addTo(map);
}

// Draw the full Grand Parkway route as a subdued base line beneath the highlight
async function addBaseRoute(map) {
  let route;
  try {
    const res = await fetch(ROUTE_URL);
    if (!res.ok) throw new Error(res.statusText);
    route = await res.json();
  } catch (err) {
    console.error('Failed to load SH 99 route layer:', err);
    return;
  }

  // ── Constructed / open base: actual built roadbed centerline (thick gray) ──
  // Drawn first so it sits beneath the tracker overlay. Source: TxDOT_Roadways
  // SH 99 roadbed lines — the authoritative "what physically exists".
  try {
    const bres = await fetch(BUILT_URL);
    if (bres.ok) {
      map.addSource(BUILT_SRC, { type: 'geojson', data: await bres.json() });
      map.addLayer({
        id: BUILT_CASE_ID, type: 'line', source: BUILT_SRC,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#0b1f33', 'line-width': 9, 'line-opacity': 0.45 }
      });
      map.addLayer({
        id: BUILT_LINE_ID, type: 'line', source: BUILT_SRC,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#9aa0a6', 'line-width': 6, 'line-opacity': 0.95 }
      });
    }
  } catch (err) {
    console.error('Failed to load SH 99 built-road base:', err);
  }

  map.addSource(ROUTE_SRC, { type: 'geojson', data: route });

  // Tracker overlay, driven by `style_class`:
  //   planned → steel-blue (in the TxDOT project tracker, not yet built)
  //   updated → amber-yellow (a real tracker change our diff caught ≤30d)
  // `constructed` features are filtered OUT — the gray built-road base already
  // shows what's open, so we don't double-draw them. line-sort-key floats
  // updated above planned.
  const NOT_BUILT = ['!=', ['get', 'style_class'], 'constructed'];
  const COLOR = ['match', ['get', 'style_class'],
    'updated', '#ffd400',
    'planned', '#4aa3ff',
    /* default */ '#4aa3ff'];
  const WIDTH = ['match', ['get', 'style_class'],
    'updated', 4,
    'planned', 2.5,
    2.5];
  const CASE_WIDTH = ['match', ['get', 'style_class'],
    'updated', 6.5,
    'planned', 4.5,
    4.5];
  const SORT = ['match', ['get', 'style_class'], 'planned', 1, 'updated', 2, 1];

  // dark casing for contrast on satellite imagery
  map.addLayer({
    id: ROUTE_CASE_ID, type: 'line', source: ROUTE_SRC, filter: NOT_BUILT,
    layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': SORT },
    paint: { 'line-color': '#0b1f33', 'line-width': CASE_WIDTH, 'line-opacity': 0.5 }
  });

  // class-colored route line
  map.addLayer({
    id: ROUTE_LINE_ID, type: 'line', source: ROUTE_SRC, filter: NOT_BUILT,
    layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': SORT },
    paint: { 'line-color': COLOR, 'line-width': WIDTH, 'line-opacity': 0.95 }
  });

  map.on('click', ROUTE_LINE_ID, (e) => {
    if (!e.features?.length) return;
    openRoutePopup(map, e.lngLat, routePopupHtml(e.features[0].properties), '360px');
  });
  map.on('mouseenter', ROUTE_LINE_ID, () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', ROUTE_LINE_ID, () => { map.getCanvas().style.cursor = ''; });

  // Built-road base popup — simple "constructed / open" tag
  if (map.getLayer(BUILT_LINE_ID)) {
    map.on('click', BUILT_LINE_ID, (e) => {
      if (!e.features?.length) return;
      openRoutePopup(map, e.lngLat,
        '<div class="route-popup"><h3>SH 99 · Grand Parkway</h3>'
        + '<table><tr><td>Status</td><td><b>Constructed / open</b></td></tr>'
        + '<tr><td>Source</td><td>TxDOT roadbed centerline</td></tr></table></div>', '260px');
    });
  }

  window.toggleRoute = (visible) => {
    const v = visible ? 'visible' : 'none';
    [BUILT_CASE_ID, BUILT_LINE_ID, ROUTE_CASE_ID, ROUTE_LINE_ID].forEach((id) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v);
    });
  };

  console.log(`SH 99 route added: ${route.features.length} tracker segments + built-road base`);
}

export async function addRouteUpdate(map) {
  // full route first (sits below the highlight)
  await addBaseRoute(map);

  let data;
  try {
    const res = await fetch(DATA_URL);
    if (!res.ok) throw new Error(res.statusText);
    data = await res.json();
  } catch (err) {
    console.error('Failed to load SH 99 Segment C update layer:', err);
    return null;
  }

  map.addSource(SRC_ID, { type: 'geojson', data });

  // 1) soft red glow underneath
  map.addLayer({
    id: GLOW_ID, type: 'line', source: SRC_ID,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': GLOW, 'line-width': 11, 'line-opacity': 0.45, 'line-blur': 4 }
  });

  // 2) solid bright amber base
  map.addLayer({
    id: LINE_ID, type: 'line', source: SRC_ID,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': HIGHLIGHT, 'line-width': 4 }
  });

  // 3) animated white dash on top (movement = "active / new")
  map.addLayer({
    id: DASH_ID, type: 'line', source: SRC_ID,
    layout: { 'line-cap': 'butt', 'line-join': 'round' },
    paint: { 'line-color': '#ffffff', 'line-width': 3, 'line-opacity': 0.9 }
  });

  animateDash(map);

  // click popup
  map.on('click', LINE_ID, (e) => {
    if (!e.features?.length) return;
    openRoutePopup(map, e.lngLat, popupHtml(e.features[0].properties), '360px');
  });
  map.on('mouseenter', LINE_ID, () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', LINE_ID, () => { map.getCanvas().style.cursor = ''; });

  // expose a toggle for the banner checkbox
  window.toggleSegmentC = (visible) => {
    const v = visible ? 'visible' : 'none';
    [GLOW_ID, LINE_ID, DASH_ID].forEach((id) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v);
    });
  };

  // let the banner fly to the update
  window.flyToSegmentC = () => {
    const b = new mapboxgl.LngLatBounds();
    data.features.forEach((f) => {
      const walk = (c) => Array.isArray(c[0]) ? c.forEach(walk) : b.extend(c);
      walk(f.geometry.coordinates);
    });
    map.fitBounds(b, { padding: { top: 90, bottom: 60, left: 60, right: 360 }, duration: 1200, maxZoom: 14 });
  };

  // Legend checkboxes filter each class (gray / blue / yellow) independently.
  window.toggleRouteClass = (cls, on) => {
    if (!(cls in routeEnabled)) return;
    routeEnabled[cls] = !!on;
    applyRouteFilter(map);
  };

  console.log(`SH 99 Segment C update layer added (${data.features.length} segments)`);
  return data;
}

// marching-ants dash animation
const DASH_SEQ = [
  [0, 4, 3], [0.5, 4, 2.5], [1, 4, 2], [1.5, 4, 1.5], [2, 4, 1],
  [2.5, 4, 0.5], [3, 4, 0], [0, 0.5, 3, 3.5], [0, 1, 3, 3],
  [0, 1.5, 3, 2.5], [0, 2, 3, 2], [0, 2.5, 3, 1.5], [0, 3, 3, 1], [0, 3.5, 3, 0.5]
];
function animateDash(map) {
  let step = 0;
  function frame() {
    if (!map.getLayer(DASH_ID)) return;
    step = (step + 1) % DASH_SEQ.length;
    map.setPaintProperty(DASH_ID, 'line-dasharray', DASH_SEQ[step]);
    setTimeout(() => requestAnimationFrame(frame), 80);
  }
  frame();
}
